import { useMemo } from "react";
import { AverageRatingChart } from "../player/PlayerStats/AverageRatingChart";
import { performanceCards } from "./utils/performanceIcons";
import { chunkSections, StatsSection } from "./StatsSection";
import {
  StatsHeaderBar,
  PerformanceCards,
  StatsSkeleton,
} from "./player-stats";
import { useStatsData, useBuildSectionItems } from "./hooks";

const PlayerStatsGoalkeeper = ({
  seasonStats,
  seasonOptions,
  selectedSeasonId,
  setSelectedSeasonId,
  statsLoading,
  ratingChartData,
  selectedMatch,
  ratingsLoading,
  ratingsError,
}) => {
  // Use shared hook for stats data extraction (no goalkeeper filtering for GK view)
  const {
    blocks,
    teamName: displayTeamName,
    teamLogo: displayTeamImage,
    seasonName: displaySeasonName,
    stats,
    startsValue,
    appearancesValue,
    shouldShowStartsAppearances,
    shouldShowAppearancesCard,
    hasStats: hasStatsData,
  } = useStatsData(seasonStats, { filterGoalkeeper: false });

  // Use shared hook for building section items with progress bars
  const { buildSectionItems } = useBuildSectionItems();

  // Sort stat blocks to control column placement (left: Overview/Pass/Defence, right: Shots/Drib-Possession)
  const sortedBlocks = useMemo(() => {
    const order = {
      Overview: 0,
      Pass: 1,
      Defence: 2,
      Shots: 3,
      "Drib-Possession": 4,
      Goalkeeper: 5,
    };
    return [...blocks].sort((a, b) => {
      const aPos = order[a?.name] ?? 99;
      const bPos = order[b?.name] ?? 99;
      return aPos - bPos;
    });
  }, [blocks]);

  // Split blocks into two columns without forcing equal heights
  const columns = useMemo(() => {
    const leftNames = ["Overview", "Goalkeeper", "Pass"];
    const rightNames = ["Shots", "Drib-Possession", "Defence"];

    const leftMap = new Map();
    const rightMap = new Map();
    const extras = [];

    // Collect blocks
    sortedBlocks.forEach((block) => {
      if (leftNames.includes(block.name)) {
        leftMap.set(block.name, block);
      } else if (rightNames.includes(block.name)) {
        rightMap.set(block.name, block);
      } else {
        extras.push(block);
      }
    });

    // Build ordered columns
    const left = leftNames.map((name) => leftMap.get(name)).filter(Boolean);

    const right = rightNames.map((name) => rightMap.get(name)).filter(Boolean);

    // Balance extras (if any)
    extras.forEach((block) => {
      if (left.length <= right.length) left.push(block);
      else right.push(block);
    });

    return { left, right };
  }, [sortedBlocks]);

  // Calculate derived stats for goalkeeper-specific cards
  const savesMade = useMemo(() => {
    const block = blocks.find((b) => b.name === "Defence");
    const stat = block?.stats?.find((s) => s.key === "saves");
    return stat?.value || 0;
  }, [blocks]);

  const goalsConceded = useMemo(() => {
    const block = blocks.find((b) => b.name === "Defence");
    const stat = block?.stats?.find((s) => s.key === "goals_conceded");
    return stat?.value || 0;
  }, [blocks]);

  const minutesPlayed = useMemo(() => {
    const block = blocks.find((b) => b.name === "Overview");
    const stat = block?.stats?.find((s) => s.key === "minutes_played");
    return stat?.value || 0;
  }, [blocks]);

  const totalShots = savesMade + goalsConceded;
  const savePercentage = totalShots > 0 ? (savesMade / totalShots) * 100 : 0;
  const goalsConcededPer90 =
    minutesPlayed > 0 ? (goalsConceded / minutesPlayed) * 90 : 0;

  // Goalkeeper-specific performance cards
  const goalkeeperCards = useMemo(
    () => [
      {
        key: "appearances",
        label: "Appearances",
        icon: performanceCards[0].icon,
        getValue: () => appearancesValue || 0,
      },
      {
        key: "cleanSheets",
        label: "Clean Sheets",
        icon: performanceCards[1].icon,
        getValue: (s) => s.clean_sheets || 0,
      },
      {
        key: "saves",
        label: "Saves",
        icon: performanceCards[2].icon,
        getValue: (s) => s.saves || 0,
      },
      {
        key: "goalsCompleted",
        label: "Goals Conceded",
        icon: performanceCards[3].icon,
        getValue: (s) => s.goals_conceded || 0,
      },
      {
        key: "savePercentage",
        label: "Save %",
        icon: performanceCards[4].icon,
        getValue: () => `${savePercentage.toFixed(1)}%`,
      },
      {
        key: "concededPer90",
        label: "Conceded/90",
        icon: performanceCards[5].icon,
        getValue: () => goalsConcededPer90.toFixed(2),
      },
    ],
    [appearancesValue, savePercentage, goalsConcededPer90]
  );

  // Filter goalkeeper cards based on validation
  const filteredGoalkeeperCards = useMemo(() => {
    return goalkeeperCards.filter((card) => {
      if (card.key === "appearances") {
        return shouldShowAppearancesCard;
      }
      return true;
    });
  }, [goalkeeperCards, shouldShowAppearancesCard]);

  // Check if stats are empty
  const hasStats = !statsLoading && hasStatsData;

  return (
    <div className="w-full mx-auto py-6 bg-white space-y-6">
      {/* Stats sections - Only show if stats are available */}
      {statsLoading ? (
        <StatsSkeleton
          ratingChartData={ratingChartData}
          statsLoading={statsLoading}
          ratingsLoading={ratingsLoading}
          selectedMatch={selectedMatch}
        />
      ) : hasStats ? (
        <>
          {/* Header Info Bar — match the prop names StatsHeaderBar actually
              destructures (starts/appearances/minutes/touches/show…). The
              previous *Value variants fell through to the default `= 0`
              parameters, silently rendering the header as all-zeros even
              when the backend returned real goalkeeper stats. */}
          <StatsHeaderBar
            teamLogo={displayTeamImage}
            teamName={displayTeamName}
            seasonName={displaySeasonName}
            starts={startsValue}
            appearances={appearancesValue}
            minutes={minutesPlayed}
            touches={0}
            showStartsAppearances={shouldShowStartsAppearances}
          />

          {/* Performance Summary Cards */}
          <PerformanceCards
            cards={filteredGoalkeeperCards}
            stats={stats}
            className="-ml-4 md:ml-0"
          />

          {/* Average Rating Chart - Always visible */}
          <AverageRatingChart
            chartData={ratingChartData}
            isLoading={statsLoading || ratingsLoading}
            selectedMatch={selectedMatch}
          />

          <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-6">
            <div className="flex-1 flex flex-col gap-6">
              {columns.left.map((block) => (
                <StatsSection
                  key={block.name}
                  title={block.name}
                  items={buildSectionItems(block)}
                />
              ))}
            </div>

            <div className="flex-1 flex flex-col gap-6">
              {columns.right.map((block) => (
                <StatsSection
                  key={block.name}
                  title={block.name}
                  items={buildSectionItems(block)}
                />
              ))}
            </div>
          </div>
        </>
      ) : null}

      {/* Average Rating Chart - Always visible (when no stats) */}
      {!statsLoading && !hasStats && (
        <AverageRatingChart
          chartData={ratingChartData}
          isLoading={statsLoading || ratingsLoading}
          selectedMatch={selectedMatch}
        />
      )}
    </div>
  );
};

export default PlayerStatsGoalkeeper;
